import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon.jsx';

const { FiInfo, FiChevronDown, FiChevronUp } = FiIcons;

const Assumptions = () => {
  const [isOpen, setIsOpen] = useState(false);

  const assumptions = [
    {
      title: 'Fuel-only estimate',
      description: 'This calculator only estimates fuel costs based on your diesel consumption and usage patterns.'
    },
    {
      title: 'Excluded costs',
      description: 'Does not include capital expenditure, financing costs, major overhauls, theft, or downtime costs.'
    },
    {
      title: 'Variable load',
      description: 'Real-world electrical load varies throughout operation; this uses your average load estimate.'
    },
    {
      title: 'Decision support tool',
      description: 'Use results as a decision-support tool and rough estimate, not as a guarantee of actual costs.'
    },
    {
      title: 'Generator efficiency',
      description: 'Calculations assume your fuel consumption data reflects your generator\'s actual efficiency.'
    },
    {
      title: 'All-in estimates',
      description: 'Optional all-in estimates add 20-40% to account for maintenance, oil, filters, repairs, and staff time.'
    }
  ];

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-4 focus:ring-violet-200"
            aria-expanded={isOpen}
            aria-controls="assumptions-content"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon 
                  icon={FiInfo} 
                  className="text-2xl text-violet-600" 
                  aria-hidden="true"
                />
                <h2 className="text-2xl font-bold text-gray-900">
                  Assumptions & Limitations
                </h2>
              </div>
              <SafeIcon 
                icon={isOpen ? FiChevronUp : FiChevronDown} 
                className="text-xl text-gray-500" 
                aria-hidden="true"
              />
            </div>
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                id="assumptions-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-gray-200"
              >
                <div className="px-8 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {assumptions.map((assumption, index) => (
                      <motion.div
                        key={assumption.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="p-4 bg-gray-50 rounded-lg"
                      >
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {assumption.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {assumption.description}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">
                      Important Note
                    </h3>
                    <p className="text-sm text-blue-800">
                      This tool provides estimates for educational and decision-support purposes only. 
                      Actual costs may vary significantly based on local conditions, generator efficiency, 
                      maintenance practices, and operational factors not captured in this simple calculation.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Assumptions;